// ═══════════════════════════════════════════════════════════
// CustomerCard — картка клієнта в сітці
// Аватар з градієнтом, категорія, баланс (продано/сплачено)
// Контекстне меню: записати, фінанси, редагувати, видалити
// ═══════════════════════════════════════════════════════════
import React, { useState, useRef, useEffect } from 'react';
import { useT } from '../contexts/I18nContext';

const AVATAR_GRADIENTS = [
  ['#6366f1', '#8b5cf6'], ['#0ea5e9', '#6366f1'], ['#10b981', '#0ea5e9'],
  ['#f59e0b', '#ef4444'], ['#ec4899', '#8b5cf6'], ['#14b8a6', '#10b981'],
  ['#f97316', '#ec4899'], ['#84cc16', '#0ea5e9'],
];

const GENDER_ICON = { male: '♂', female: '♀', other: '' };

function getInitials(name) {
  return name.trim().split(/\s+/).map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
}

function getGradient(name) {
  const idx = (name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % AVATAR_GRADIENTS.length;
  return AVATAR_GRADIENTS[idx];
}

function CustomerCard({ customer, categories, importanceLevels, onView, onEdit, onDelete, isDeleting, onBook, onOrder, onSubscription, viewMode }) {
  const { t } = useT();
  const [sellOpen, setSellOpen] = useState(false);
  const sellRef = useRef(null);
  useEffect(() => {
    if (!sellOpen) return;
    const h = (e) => { if (sellRef.current && !sellRef.current.contains(e.target)) setSellOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [sellOpen]);
  // Dynamic lookup from props
  const cat = (categories || []).find(c => c.name === customer.category) || {
    bgColor: '#f1f5f9', textColor: '#475569', borderColor: '#cbd5e1', icon: '👤',
  };
  const imp = (importanceLevels || []).find(i => i.name === customer.importance);

  const [c1, c2] = getGradient(customer.name);

  const birthDate = customer.birthDay && customer.birthMonth && customer.birthYear
    ? `${customer.birthDay}.${customer.birthMonth}.${customer.birthYear}`
    : null;

  if (viewMode === 'list') {
    return (
      <div className="customer-list-row" onClick={onView || onEdit} style={{ cursor: 'pointer' }}>
        {/* Avatar */}
        <div className="cl-avatar-wrap">
          {customer.avatar ? (
            <img src={customer.avatar} alt={customer.name} className="cl-avatar-img" />
          ) : (
            <div className="cl-avatar-initials" style={{ background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)` }}>
              {getInitials(customer.name)}
            </div>
          )}
          {imp && <span className="importance-dot" style={{ background: imp.color }} title={t('custCard.importance').replace('{name}', imp.name)} />}
        </div>

        {/* Name + badges */}
        <div className="cl-name-col">
          <div className="cl-name-row">
            <span className="cl-name clickable-name">{customer.docNumber && <span className="doc-num">#{String(customer.docNumber).padStart(3, '0')}</span>}{customer.name}</span>
            {customer.gender && customer.gender !== 'other' && GENDER_ICON[customer.gender] && (
              <span className="gender-icon">{GENDER_ICON[customer.gender]}</span>
            )}
          </div>
          <div className="card-badges">
            <span className="category-badge" style={{ background: cat.bgColor, color: cat.textColor, borderColor: cat.borderColor }}>
              {cat.icon} {customer.category}
            </span>
            {customer.discount > 0 && <span className="discount-badge">🏷 -{customer.discount}%</span>}
            {customer.forbidOnlineBooking && <span className="forbid-badge" title={t('custCard.forbidOnlineBooking')}>🚫</span>}
          </div>
        </div>

        {/* Contact */}
        <div className="cl-contact-col">
          {customer.phone && <div className="cl-contact-row"><span className="detail-icon">📱</span>{customer.phone}</div>}
          {customer.additionalPhone && <div className="cl-contact-row"><span className="detail-icon">📞</span>{customer.additionalPhone}</div>}
          {customer.email && <div className="cl-contact-row"><span className="detail-icon">✉️</span><span className="detail-truncate">{customer.email}</span></div>}
        </div>

        {/* Actions */}
        <div className="cl-actions-col" onClick={e => e.stopPropagation()}>
          <div className="btn-sell-wrap" ref={sellRef}>
            <button className="btn-book" onClick={() => setSellOpen(!sellOpen)}>
              <span className="btn-book-text">{t('custCard.sell')}</span>
              <span style={{ fontSize: '0.7rem', marginLeft: 4 }}>▾</span>
            </button>
            {sellOpen && (
              <div className="btn-sell-dropdown">
                {onOrder && <button onMouseDown={() => { setSellOpen(false); onOrder(); }}>{t('custCard.sellProduct')}</button>}
                <button onMouseDown={() => { setSellOpen(false); onBook(); }}>{t('custCard.sellService')}</button>
                {onSubscription && <button onMouseDown={() => { setSellOpen(false); onSubscription(); }}>{t('custCard.sellSubscription')}</button>}
              </div>
            )}
          </div>
          {onEdit && <button className="action-btn edit-btn" onClick={onEdit} title={t('custCard.edit')}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          </button>}
          {onDelete && <button className="action-btn delete-btn" onClick={onDelete} disabled={isDeleting} title={t('custCard.delete')}>
            {isDeleting ? <div className="spinner-sm" /> : <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>}
          </button>}
        </div>
      </div>
    );
  }

  return (
    <div className="customer-card" onClick={onView || onEdit} style={{ cursor: 'pointer' }}>
      {/* Top */}
      <div className="card-top">
        <div className="card-avatar-area">
          {customer.avatar ? (
            <img src={customer.avatar} alt={customer.name} className="card-avatar-img" />
          ) : (
            <div
              className="card-avatar-initials"
              style={{ background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)` }}
            >
              {getInitials(customer.name)}
            </div>
          )}
          {imp && (
            <span
              className="importance-dot"
              style={{ background: imp.color }}
              title={t('custCard.importance').replace('{name}', imp.name)}
            />
          )}
        </div>

        <div className="card-info">
          <div className="card-name-row">
            <h3 className="card-name clickable-name" title={customer.name}>{customer.name}</h3>
            {customer.gender && customer.gender !== 'other' && GENDER_ICON[customer.gender] && (
              <span className="gender-icon">{GENDER_ICON[customer.gender]}</span>
            )}
          </div>
          <div className="card-badges">
            <span
              className="category-badge"
              style={{ background: cat.bgColor, color: cat.textColor, borderColor: cat.borderColor }}
            >
              {cat.icon} {customer.category}
            </span>
            {customer.discount > 0 && (
              <span className="discount-badge">🏷 -{customer.discount}%</span>
            )}
            {customer.forbidOnlineBooking && (
              <span className="forbid-badge" title={t('custCard.forbidOnlineBooking')}>🚫</span>
            )}
          </div>
        </div>

        <div className="card-actions" onClick={e => e.stopPropagation()}>
          <div className="btn-sell-wrap" ref={sellRef}>
            <button className="btn-book" onClick={() => setSellOpen(!sellOpen)}>
              <span className="btn-book-text">{t('custCard.sell')}</span>
              <span style={{ fontSize: '0.7rem', marginLeft: 4 }}>▾</span>
            </button>
            {sellOpen && (
              <div className="btn-sell-dropdown">
                {onOrder && <button onMouseDown={() => { setSellOpen(false); onOrder(); }}>{t('custCard.sellProduct')}</button>}
                <button onMouseDown={() => { setSellOpen(false); onBook(); }}>{t('custCard.sellService')}</button>
                {onSubscription && <button onMouseDown={() => { setSellOpen(false); onSubscription(); }}>{t('custCard.sellSubscription')}</button>}
              </div>
            )}
          </div>
          {onEdit && <button className="action-btn edit-btn" onClick={onEdit} title={t('custCard.edit')}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          </button>}
          {onDelete && <button className="action-btn delete-btn" onClick={onDelete} disabled={isDeleting} title={t('custCard.delete')}>
            {isDeleting ? <div className="spinner-sm" /> : <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>}
          </button>}
        </div>
      </div>

      {/* Details */}
      <div className="card-details">
        <div className="detail-row">
          <span className="detail-icon">📱</span>
          <span>{customer.phone || '—'}</span>
        </div>
        {customer.additionalPhone && (
          <div className="detail-row">
            <span className="detail-icon">📞</span>
            <span>{customer.additionalPhone}</span>
          </div>
        )}
        {customer.email && (
          <div className="detail-row">
            <span className="detail-icon">✉️</span>
            <span className="detail-truncate">{customer.email}</span>
          </div>
        )}
        {birthDate && (
          <div className="detail-row">
            <span className="detail-icon">🎂</span>
            <span>{birthDate}</span>
          </div>
        )}
        {customer.cardNumber && (
          <div className="detail-row">
            <span className="detail-icon">💳</span>
            <span>{customer.cardNumber}</span>
          </div>
        )}
        {customer.note && (
          <div className="detail-row note-row">
            <span className="detail-icon">📝</span>
            <span className="detail-truncate">{customer.note}</span>
          </div>
        )}
      </div>

    </div>
  );
}

export default React.memo(CustomerCard);
